import { useState, useEffect } from "react";
import type { PrinterProfile, Review } from "../lib/data";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { Progress } from "./ui/progress";
import {
	Star,
	MapPin,
	Clock,
	Shield,
	Ruler,
	Printer,
	ArrowLeft,
	Award,
	Package,
	ThumbsUp,
	Zap,
	CheckCircle2,
	Camera,
} from "lucide-react";

interface PrinterProfilePageProps {
	printer: PrinterProfile;
	onBack: () => void;
	onRequestPrint: (printerId: string) => void;
}

export function PrinterProfilePage({ printer, onBack, onRequestPrint }: PrinterProfilePageProps) {
	const [reviews, setReviews] = useState<Review[]>([]);
	const [loadingReviews, setLoadingReviews] = useState(true);

	useEffect(() => {
		fetch(`/api/printers/${printer.id}/reviews`)
			.then((r) => r.json())
			.then(setReviews)
			.finally(() => setLoadingReviews(false));
	}, [printer.id]);

	const avgQuality = reviews.length
		? reviews.reduce((s, r) => s + r.qualityRating, 0) / reviews.length
		: 0;
	const avgSpeed = reviews.length
		? reviews.reduce((s, r) => s + r.speedRating, 0) / reviews.length
		: 0;
	const avgComm = reviews.length
		? reviews.reduce((s, r) => s + r.communicationRating, 0) / reviews.length
		: 0;

	const ratingDist = [5, 4, 3, 2, 1].map((stars) => ({
		stars,
		count: reviews.filter((r) => Math.round(r.rating) === stars).length,
		pct: reviews.length
			? (reviews.filter((r) => Math.round(r.rating) === stars).length / reviews.length) * 100
			: 0,
	}));

	return (
		<div className="space-y-6">
			{/* Back button */}
			<Button variant="ghost" size="sm" onClick={onBack} className="gap-2 text-muted-foreground hover:text-foreground">
				<ArrowLeft className="w-4 h-4" />
				Back to search
			</Button>

			{/* Hero section */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Main info */}
				<div className="lg:col-span-2 space-y-4">
					<Card>
						<CardContent className="p-6">
							<div className="flex items-start gap-5">
								<div className="relative flex-shrink-0">
									<div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center border border-border">
										<Printer className="w-10 h-10 text-primary/70" />
									</div>
									{printer.verified && (
										<div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
											<Shield className="w-3.5 h-3.5 text-white" />
										</div>
									)}
								</div>
								<div className="flex-1">
									<div className="flex items-start justify-between">
										<div>
											<h1 className="text-2xl font-bold">{printer.name}</h1>
											<p className="text-muted-foreground">{printer.ownerName}</p>
										</div>
										<Button onClick={() => onRequestPrint(printer.id)} size="lg" className="gap-2">
											<Package className="w-4 h-4" />
											Request Print
										</Button>
									</div>

									<div className="flex items-center gap-4 mt-3">
										<div className="flex items-center gap-1">
											<Star className="w-4 h-4 fill-amber-400 text-amber-400" />
											<span className="font-semibold">{printer.rating}</span>
											<span className="text-muted-foreground text-sm">({printer.reviewCount} reviews)</span>
										</div>
										<Separator orientation="vertical" className="h-4" />
										<div className="flex items-center gap-1 text-sm text-muted-foreground">
											<MapPin className="w-3.5 h-3.5" />
											{printer.location.city}, {printer.location.state}
										</div>
										<Separator orientation="vertical" className="h-4" />
										<div className="flex items-center gap-1 text-sm text-muted-foreground">
											<Clock className="w-3.5 h-3.5" />
											Responds {printer.responseTime}
										</div>
									</div>

									<p className="mt-4 text-sm text-muted-foreground leading-relaxed">{printer.bio}</p>

									<div className="flex flex-wrap gap-2 mt-4">
										{printer.specialties.map((s) => (
											<Badge key={s} variant="secondary">{s}</Badge>
										))}
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Stats cards */}
					<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
						<Card>
							<CardContent className="p-4 text-center">
								<Award className="w-5 h-5 mx-auto text-primary mb-1" />
								<div className="text-2xl font-bold">{printer.reputationScore}%</div>
								<div className="text-xs text-muted-foreground">Reputation</div>
							</CardContent>
						</Card>
						<Card>
							<CardContent className="p-4 text-center">
								<Package className="w-5 h-5 mx-auto text-primary mb-1" />
								<div className="text-2xl font-bold">{printer.completedJobs}</div>
								<div className="text-xs text-muted-foreground">Jobs Done</div>
							</CardContent>
						</Card>
						<Card>
							<CardContent className="p-4 text-center">
								<Zap className="w-5 h-5 mx-auto text-primary mb-1" />
								<div className="text-2xl font-bold">{printer.avgPrintTime}h</div>
								<div className="text-xs text-muted-foreground">Avg Print Time</div>
							</CardContent>
						</Card>
						<Card>
							<CardContent className="p-4 text-center">
								<CheckCircle2 className="w-5 h-5 mx-auto text-primary mb-1" />
								<div className="text-2xl font-bold">
									{new Date(printer.joinedDate).getFullYear()}
								</div>
								<div className="text-xs text-muted-foreground">Member Since</div>
							</CardContent>
						</Card>
					</div>

					{/* Capabilities */}
					<Card>
						<CardContent className="p-6">
							<h2 className="font-semibold text-lg mb-4">Capabilities</h2>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<div>
									<div className="text-sm font-medium mb-2 flex items-center gap-2">
										<Printer className="w-4 h-4 text-muted-foreground" />
										Printer Model
									</div>
									<p className="text-sm text-muted-foreground">{printer.printerModel}</p>
								</div>
								<div>
									<div className="text-sm font-medium mb-2 flex items-center gap-2">
										<Ruler className="w-4 h-4 text-muted-foreground" />
										Max Build Size
									</div>
									<p className="text-sm text-muted-foreground">
										{printer.maxBuildSize.x} x {printer.maxBuildSize.y} x {printer.maxBuildSize.z} mm
									</p>
								</div>
								<div className="sm:col-span-2">
									<div className="text-sm font-medium mb-2">Supported Materials</div>
									<div className="flex flex-wrap gap-2">
										{printer.materials.map((mat) => (
											<Badge key={mat} variant="outline" className="gap-1">
												{mat}
												<span className="text-muted-foreground">
													${printer.pricePerGram[mat]?.toFixed(2)}/g
												</span>
											</Badge>
										))}
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Sample photos */}
					<Card>
						<CardContent className="p-6">
							<h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
								<Camera className="w-5 h-5 text-muted-foreground" />
								Sample Work
							</h2>
							<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
								{printer.samplePhotos.map((photo, i) => (
									<div
										key={i}
										className="aspect-square rounded-xl bg-muted border border-border overflow-hidden"
									>
										<img
											src={photo}
											alt={`Sample work ${i + 1}`}
											className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
											loading="lazy"
										/>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Sidebar - Reviews */}
				<div className="space-y-4">
					<Card>
						<CardContent className="p-6">
							<h2 className="font-semibold text-lg mb-4">Ratings & Reviews</h2>

							{/* Overall rating */}
							<div className="text-center mb-4">
								<div className="text-4xl font-bold">{printer.rating}</div>
								<div className="flex justify-center gap-0.5 mt-1">
									{[1, 2, 3, 4, 5].map((s) => (
										<Star
											key={s}
											className={`w-4 h-4 ${
												s <= Math.round(printer.rating)
													? "fill-amber-400 text-amber-400"
													: "text-muted"
											}`}
										/>
									))}
								</div>
								<p className="text-sm text-muted-foreground mt-1">{printer.reviewCount} reviews</p>
							</div>

							<Separator className="my-4" />

							{/* Rating distribution */}
							<div className="space-y-2">
								{ratingDist.map((d) => (
									<div key={d.stars} className="flex items-center gap-2 text-sm">
										<span className="w-3 text-muted-foreground">{d.stars}</span>
										<Star className="w-3 h-3 fill-amber-400 text-amber-400" />
										<Progress value={d.pct} className="h-2 flex-1" />
										<span className="w-6 text-right text-muted-foreground text-xs">{d.count}</span>
									</div>
								))}
							</div>

							<Separator className="my-4" />

							{/* Category ratings */}
							<div className="space-y-3">
								<div className="flex justify-between text-sm">
									<span className="text-muted-foreground">Quality</span>
									<span className="font-medium">{avgQuality.toFixed(1)}</span>
								</div>
								<div className="flex justify-between text-sm">
									<span className="text-muted-foreground">Speed</span>
									<span className="font-medium">{avgSpeed.toFixed(1)}</span>
								</div>
								<div className="flex justify-between text-sm">
									<span className="text-muted-foreground">Communication</span>
									<span className="font-medium">{avgComm.toFixed(1)}</span>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Individual reviews */}
					<div className="space-y-3">
						{loadingReviews ? (
							<Card>
								<CardContent className="p-6 text-center text-muted-foreground text-sm">
									Loading reviews...
								</CardContent>
							</Card>
						) : reviews.length === 0 ? (
							<Card>
								<CardContent className="p-6 text-center text-muted-foreground text-sm">
									No reviews yet
								</CardContent>
							</Card>
						) : (
							reviews.map((review) => (
								<Card key={review.id}>
									<CardContent className="p-4">
										<div className="flex items-start justify-between">
											<div className="flex items-center gap-2">
												<div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
													{review.authorName.charAt(0)}
												</div>
												<div>
													<div className="text-sm font-medium">{review.authorName}</div>
													<div className="text-xs text-muted-foreground">
														{new Date(review.createdAt).toLocaleDateString()}
													</div>
												</div>
											</div>
											<div className="flex gap-0.5">
												{[1, 2, 3, 4, 5].map((s) => (
													<Star
														key={s}
														className={`w-3 h-3 ${
															s <= review.rating
																? "fill-amber-400 text-amber-400"
																: "text-muted"
														}`}
													/>
												))}
											</div>
										</div>
										<p className="text-sm text-muted-foreground mt-2 leading-relaxed">
											{review.comment}
										</p>
										<div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
											<ThumbsUp className="w-3 h-3" />
											{review.helpful} found this helpful
										</div>
									</CardContent>
								</Card>
							))
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
